/**
 * Color Foundation — the single source of truth for all color values.
 *
 * TODAY: mirrors Tailwind CSS v3 color palette exactly.
 * TOMORROW: can be swapped to any color system without changing consumers.
 *
 * Every file that needs a color hex value imports from here — never hardcodes.
 * The shade structure (50–950) is the public API contract.
 *
 * STRUCTURE:
 *   colors.<family>[<shade>]  →  hex string
 *   e.g. colors.emerald[400]  →  '#34d399'
 *
 * FAMILIES INCLUDED:
 *   Neutrals:  slate, gray, zinc, neutral, stone
 *   Warm:      red, orange, amber, yellow
 *   Nature:    emerald, teal
 *   Cool:      sky, blue, indigo
 *   Creative:  violet, purple, pink, rose
 *
 * UTILITIES:
 *   hexToRgb, hexToHsl, hslToHex, luminance
 *   generateHarmony (complementary, analogous, triadic, monochromatic)
 *
 * SEMANTIC EXPORTS:
 *   TEXT_ACCENT_COLORS — the 8-color toolbar set (sacred, do not change values)
 */

// ─── Color Scales ───────────────────────────────────────────────────
// Each family: { 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950 }

export const colors = {

  // ── Neutrals ──────────────────────────────────────────────────────

  slate: {
    50:  '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1',
    400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155',
    800: '#1e293b', 900: '#0f172a', 950: '#020617',
  },

  gray: {
    50:  '#f9fafb', 100: '#f3f4f6', 200: '#e5e7eb', 300: '#d1d5db',
    400: '#9ca3af', 500: '#6b7280', 600: '#4b5563', 700: '#374151',
    800: '#1f2937', 900: '#111827', 950: '#030712',
  },

  zinc: {
    50:  '#fafafa', 100: '#f4f4f5', 200: '#e4e4e7', 300: '#d4d4d8',
    400: '#a1a1aa', 500: '#71717a', 600: '#52525b', 700: '#3f3f46',
    800: '#27272a', 900: '#18181b', 950: '#09090b',
  },

  neutral: {
    50:  '#fafafa', 100: '#f5f5f5', 200: '#e5e5e5', 300: '#d4d4d4',
    400: '#a3a3a3', 500: '#737373', 600: '#525252', 700: '#404040',
    800: '#262626', 900: '#171717', 950: '#0a0a0a',
  },

  stone: {
    50:  '#fafaf9', 100: '#f5f5f4', 200: '#e7e5e4', 300: '#d6d3d1',
    400: '#a8a29e', 500: '#78716c', 600: '#57534e', 700: '#44403c',
    800: '#292524', 900: '#1c1917', 950: '#0c0a09',
  },

  // ── Warm ──────────────────────────────────────────────────────────

  red: {
    50:  '#fef2f2', 100: '#fee2e2', 200: '#fecaca', 300: '#fca5a5',
    400: '#f87171', 500: '#ef4444', 600: '#dc2626', 700: '#b91c1c',
    800: '#991b1b', 900: '#7f1d1d', 950: '#450a0a',
  },

  orange: {
    50:  '#fff7ed', 100: '#ffedd5', 200: '#fed7aa', 300: '#fdba74',
    400: '#fb923c', 500: '#f97316', 600: '#ea580c', 700: '#c2410c',
    800: '#9a3412', 900: '#7c2d12', 950: '#431407',
  },

  amber: {
    50:  '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 300: '#fcd34d',
    400: '#fbbf24', 500: '#f59e0b', 600: '#d97706', 700: '#b45309',
    800: '#92400e', 900: '#78350f', 950: '#451a03',
  },

  yellow: {
    50:  '#fefce8', 100: '#fef9c3', 200: '#fef08a', 300: '#fde047',
    400: '#facc15', 500: '#eab308', 600: '#ca8a04', 700: '#a16207',
    800: '#854d0e', 900: '#713f12', 950: '#422006',
  },

  // ── Nature ────────────────────────────────────────────────────────

  emerald: {
    50:  '#ecfdf5', 100: '#d1fae5', 200: '#a7f3d0', 300: '#6ee7b7',
    400: '#34d399', 500: '#10b981', 600: '#059669', 700: '#047857',
    800: '#065f46', 900: '#064e3b', 950: '#022c22',
  },

  teal: {
    50:  '#f0fdfa', 100: '#ccfbf1', 200: '#99f6e4', 300: '#5eead4',
    400: '#2dd4bf', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e',
    800: '#115e59', 900: '#134e4a', 950: '#042f2e',
  },

  // ── Cool ──────────────────────────────────────────────────────────

  sky: {
    50:  '#f0f9ff', 100: '#e0f2fe', 200: '#bae6fd', 300: '#7dd3fc',
    400: '#38bdf8', 500: '#0ea5e9', 600: '#0284c7', 700: '#0369a1',
    800: '#075985', 900: '#0c4a6e', 950: '#082f49',
  },

  blue: {
    50:  '#eff6ff', 100: '#dbeafe', 200: '#bfdbfe', 300: '#93c5fd',
    400: '#60a5fa', 500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8',
    800: '#1e40af', 900: '#1e3a8a', 950: '#172554',
  },

  indigo: {
    50:  '#eef2ff', 100: '#e0e7ff', 200: '#c7d2fe', 300: '#a5b4fc',
    400: '#818cf8', 500: '#6366f1', 600: '#4f46e5', 700: '#4338ca',
    800: '#3730a3', 900: '#312e81', 950: '#1e1b4b',
  },

  // ── Creative ──────────────────────────────────────────────────────

  violet: {
    50:  '#f5f3ff', 100: '#ede9fe', 200: '#ddd6fe', 300: '#c4b5fd',
    400: '#a78bfa', 500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9',
    800: '#5b21b6', 900: '#4c1d95', 950: '#2e1065',
  },

  purple: {
    50:  '#faf5ff', 100: '#f3e8ff', 200: '#e9d5ff', 300: '#d8b4fe',
    400: '#c084fc', 500: '#a855f7', 600: '#9333ea', 700: '#7e22ce',
    800: '#6b21a8', 900: '#581c87', 950: '#3b0764',
  },

  pink: {
    50:  '#fdf2f8', 100: '#fce7f3', 200: '#fbcfe8', 300: '#f9a8d4',
    400: '#f472b6', 500: '#ec4899', 600: '#db2777', 700: '#be185d',
    800: '#9d174d', 900: '#831843', 950: '#500724',
  },

  rose: {
    50:  '#fff1f2', 100: '#ffe4e6', 200: '#fecdd3', 300: '#fda4af',
    400: '#fb7185', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c',
    800: '#9f1239', 900: '#881337', 950: '#4c0519',
  },

  // ── Absolute ──────────────────────────────────────────────────────

  white: '#ffffff',
  black: '#000000',
};


// ─── Semantic: Text Accent Colors ───────────────────────────────────
// The 8-color WYSIWYG toolbar set. These exact values are sacred.
// white + gray-400 + black + amber-300 + emerald-300 + sky-300 + violet-300 + red-300

export const TEXT_ACCENT_COLORS = [
  { id: 'white',   hex: colors.white,        label: 'White' },
  { id: 'gray',    hex: colors.gray[400],     label: 'Gray' },
  { id: 'black',   hex: colors.black,         label: 'Black' },
  { id: 'gold',    hex: colors.amber[300],    label: 'Gold' },
  { id: 'green',   hex: colors.emerald[300],  label: 'Green' },
  { id: 'blue',    hex: colors.sky[300],      label: 'Blue' },
  { id: 'violet',  hex: colors.violet[300],   label: 'Violet' },
  { id: 'rose',    hex: colors.red[300],      label: 'Rose' },
];


// ─── Representative Hues (for harmony calculations) ─────────────────
// HSL hue angle of the 500 shade for each chromatic family.

export const FAMILY_HUES = {
  red:     0,
  orange:  25,
  amber:   38,
  yellow:  48,
  emerald: 152,
  teal:    174,
  sky:     199,
  blue:    217,
  indigo:  239,
  violet:  258,
  purple:  272,
  pink:    330,
  rose:    350,
};


// ─── Utility: Hex ↔ RGB ─────────────────────────────────────────────

/**
 * Parse hex color to { r, g, b } (0–255).
 * Accepts '#RGB', '#RRGGBB', 'RGB', 'RRGGBB'.
 */
export function hexToRgb(hex) {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/**
 * Convert { r, g, b } to hex string '#rrggbb'.
 */
export function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}


// ─── Utility: Hex ↔ HSL ─────────────────────────────────────────────

/**
 * Parse hex color to { h, s, l } (h: 0–360, s/l: 0–100).
 */
export function hexToHsl(hex) {
  const { r: r8, g: g8, b: b8 } = hexToRgb(hex);
  const r = r8 / 255, g = g8 / 255, b = b8 / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: h = ((b - r) / d + 2); break;
      case b: h = ((r - g) / d + 4); break;
      default: break;
    }
    h *= 60;
  }

  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

/**
 * Convert HSL to hex string '#rrggbb'.
 * @param {number} h  Hue 0–360
 * @param {number} s  Saturation 0–100
 * @param {number} l  Lightness 0–100
 */
export function hslToHex(h, s, l) {
  const s1 = s / 100, l1 = l / 100;
  const c = (1 - Math.abs(2 * l1 - 1)) * s1;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l1 - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60)       { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else              { r = c; b = x; }
  return rgbToHex(
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255)
  );
}


// ─── Utility: Luminance ─────────────────────────────────────────────

/**
 * Relative luminance (0–1) per WCAG 2.0.
 * Useful for deciding light-on-dark vs dark-on-light text.
 */
export function luminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const [rs, gs, bs] = [r, g, b].map(v => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Is the color perceptually "dark"? (luminance < 0.2)
 */
export function isDark(hex) {
  return luminance(hex) < 0.2;
}


// ─── Utility: Color Harmony ─────────────────────────────────────────

/**
 * Generate harmony hue angles from a base hue.
 * Returns array of hue angles (0–360).
 *
 * @param {number} baseHue  0–360
 * @param {string} type  Harmony type
 * @returns {number[]}  Array of hue angles including the base
 */
export function generateHarmony(baseHue, type) {
  const norm = (h) => ((h % 360) + 360) % 360;
  switch (type) {
    case 'complementary':
      return [baseHue, norm(baseHue + 180)];
    case 'analogous':
      return [norm(baseHue - 30), baseHue, norm(baseHue + 30)];
    case 'split-complementary':
      return [baseHue, norm(baseHue + 150), norm(baseHue + 210)];
    case 'triadic':
      return [baseHue, norm(baseHue + 120), norm(baseHue + 240)];
    case 'tetradic':
      return [baseHue, norm(baseHue + 60), norm(baseHue + 180), norm(baseHue + 240)];
    case 'square':
      return [baseHue, norm(baseHue + 90), norm(baseHue + 180), norm(baseHue + 270)];
    case 'monochromatic':
      return [baseHue, baseHue];
    case 'auto':
    default:
      // Auto = analogous (safest default, always looks good)
      return [norm(baseHue - 30), baseHue, norm(baseHue + 30)];
  }
}

/**
 * Find the nearest color family for a given hue angle.
 * Useful for mapping harmony output back to named colors.
 *
 * @param {number} hue  0–360
 * @returns {string}  Family name (e.g. 'emerald', 'sky')
 */
export function nearestFamily(hue) {
  const norm = ((hue % 360) + 360) % 360;
  let best = 'red', bestDist = 360;
  for (const [family, fhue] of Object.entries(FAMILY_HUES)) {
    const dist = Math.min(Math.abs(norm - fhue), 360 - Math.abs(norm - fhue));
    if (dist < bestDist) {
      bestDist = dist;
      best = family;
    }
  }
  return best;
}
